import {
  HTMLAttributes,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Track } from 'livekit-client'
import { supportsScreenSharing } from '@livekit/components-core'

import {
  ChatIcon,
  LeaveIcon,
  RecordCircleDisabledIcon,
  RecordCircleIcon,
  UsersIcon,
} from '@/src/assets/icons'

import { MediaDeviceMenu } from './MediaDeviceMenu'
import { DisconnectButton } from '@/src/components/controls/DisconnectButton'
import { RecordButton } from '@/src/components/controls/RecordButton'
import { ReactionToggle } from '@/src/components/controls/ReactionsControl/ReactionToggle/ReactionToggle'
import { TrackToggle } from '@/src/components/controls/TrackToggle/TrackToggle'
import { ChatToggle } from '@/src/components/controls/ChatToggle'
import { StartMediaButton } from '@/src/components/controls/StartMediaButton'
import { ParticipantsButton } from '@/src/components/controls/ParticipantsButton'
import Popup from '@/src/components/popup/popup'
import InformationRecordingMeeting from '@/src/components/popup/InformationRecordingMeeting'
import MvpPopup from '@/src/components/popup/MvpPopup'
import { BrandLogo } from '@/src/components/brand/BrandLogo'
import { Spinner } from '@/src/components/Spinner'
import { MoreButton } from '@/src/components/controls/MoreButton/MoreButton'
import { Reactions } from '@/src/components/controls/ReactionsControl/Reactions/Reactions'
import { RaiseHandButton } from '@/src/components/controls/ReactionsControl/RaiseHandButton/RaiseHandButton'
import { useLocalParticipantPermissions, usePersistentUserChoices } from '@/src/hooks'
import { useGroupRemainingTime } from '@/src/hooks/useGroupRemainingTime'
import { useGroups } from '@/src/hooks/participant/useGroups'
import { useMediaQuery } from '@/src/hooks/internal'
import { useIsMobile } from '@/src/hooks/useIsMobile'

import { useMaybeLayoutContext } from '@/src/context'
import { useRoomContext } from '@/src/context'

import { mergeProps } from '@/src/utils'
import { useScreenShareProfile } from '@/src/context/screenShareProfile-context'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { closeUnavailableRecordingPopup, selectRecordState } from '@/src/store/slices/record'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectIsOpenChat } from '@/src/store/slices/chat'
import { selectRoomState, selectHiddenButtons } from '@/src/store/slices/room'
/** @public */
export type ControlBarControls = {
  reactions?: boolean
  raiseHandOnly?: boolean
  microphone?: boolean
  camera?: boolean
  chat?: boolean
  screenShare?: boolean
  leave?: boolean
  record?: boolean
  participants?: boolean
  endCall?: boolean
  media?: boolean
  skipMicWarmUp?: boolean
}

/** @public */
export interface ControlBarProps extends HTMLAttributes<HTMLDivElement> {
  variation?: 'minimal' | 'verbose' | 'textOnly'
  controls?: ControlBarControls
  showQuestionsButton?: boolean
  isQuestionsOpen?: boolean
  onToggleQuestions?: () => void
  /**
   * If `true`, the user's device choices will be persisted.
   * This will enables the user to have the same device choices when they rejoin the room.
   * @defaultValue true
   * @alpha
   */
  saveUserChoices?: boolean
  isPiP?: boolean
}

const QuestionsControlIcon = ({ fill = 'currentColor' }: { fill?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M6.6 5.3a1.9 1.9 0 113 1.5c-.8.3-1.3 1-1.3 1.8v.1"
      stroke={fill}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="11.6" r=".8" fill={fill} />
    <circle cx="8" cy="8" r="6.5" stroke={fill} strokeWidth="1.5" />
  </svg>
)

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function ControlBar({
  variation,
  controls,
  showQuestionsButton = false,
  isQuestionsOpen = false,
  onToggleQuestions,
  saveUserChoices = true,
  isPiP,
  ...props
}: ControlBarProps) {
  const isChatOpen = useAppSelector(selectIsOpenChat)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isOpenedMoreBtnPopover, setIsOpenedMoreBtnPopover] = useState(false)

  const handleToggleMoreBtnPopover = useCallback(() => {
    setIsOpenedMoreBtnPopover((prev) => !prev)
  }, [])

  const handleCloseMoreBtnPopover = useCallback(() => {
    setIsOpenedMoreBtnPopover(false)
  }, [])

  const { isMobileByWidth: isMobile, isMiddleDeviceByWidth } = useIsMobile()

  const layoutContext = useMaybeLayoutContext()
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const { spinning, isOpenUnavailableRecordingPopup } = useAppSelector(selectRecordState)
  const isAdmin = useAppSelector(selectIsAdmin)
  const hiddenButtons = useAppSelector(selectHiddenButtons)
  const { livekit_url, roomType, v } = useAppSelector(selectRoomState)
  const isTextAssistantRoom = roomType === 'textAssistant' || v === 'hr'

  const isTooLittleSpace = useMediaQuery(
    `(max-width: ${
      (isChatOpen || layoutContext?.widget?.state?.showParticipants) && !isAdmin ? 1260 : 1060
    }px)`,
  )

  const room = useRoomContext()
  const screenShareProfile = useScreenShareProfile()

  const localIsCurator = room.localParticipant?.attributes?.isCurator === 'true'

  const localPermissions = useLocalParticipantPermissions()

  const visibleControls = useMemo<ControlBarControls>(() => {
    if (!localPermissions) {
      return {
        leave: controls?.leave ?? true,
        endCall: false,
        reactions: controls?.reactions,
        raiseHandOnly: controls?.raiseHandOnly,
        skipMicWarmUp: controls?.skipMicWarmUp,
        camera: false,
        microphone: false,
        screenShare: false,
        chat: false,
        participants: localIsCurator,
        record: !isTextAssistantRoom,
        media: false,
      }
    }

    return {
      leave: (controls?.leave ?? true) && !hiddenButtons.leave,
      endCall: false,
      reactions: controls?.reactions ?? true,
      raiseHandOnly: controls?.raiseHandOnly ?? false,
      skipMicWarmUp: controls?.skipMicWarmUp,
      microphone: controls?.microphone ?? localPermissions.canPublish,
      camera: controls?.camera ?? localPermissions.canPublish,
      screenShare: (controls?.screenShare ?? localPermissions.canPublish) && !hiddenButtons.screenShare,
      chat: (controls?.chat ?? (localPermissions.canPublishData && controls?.chat)) && !hiddenButtons.chat,
      participants: controls?.participants ?? (localPermissions.canPublishData || localIsCurator),
      record: !isTextAssistantRoom,
      media: false,
    }
  }, [controls, localPermissions, hiddenButtons, localIsCurator, isTextAssistantRoom])

  const variationState = useMemo(
    () =>
      variation ||
      (isTooLittleSpace ||
      (isMiddleDeviceByWidth &&
        (isAdmin || isChatOpen || layoutContext?.widget?.state?.showParticipants))
        ? 'minimal'
        : 'verbose'),
    [
      variation,
      isTooLittleSpace,
      isMiddleDeviceByWidth,
      isAdmin,
      isChatOpen,
      layoutContext?.widget?.state?.showParticipants,
    ],
  )

  const showIcon = useMemo(
    () => variationState === 'minimal' || variationState === 'verbose',
    [variationState],
  )

  const showText = useMemo(
    () => variationState === 'textOnly' || variationState === 'verbose',
    [variationState],
  )

  const browserSupportsScreenSharing = useMemo(() => supportsScreenSharing(), [])

  const [isScreenShareEnabled, setIsScreenShareEnabled] = useState(false)

  const onScreenShareChange = useCallback(
    (enabled: boolean) => {
      setIsScreenShareEnabled(enabled)
    },
    [setIsScreenShareEnabled],
  )

  const isSidebarOpen = isChatOpen || isQuestionsOpen || !!layoutContext?.widget?.state?.showParticipants

  const htmlProps = mergeProps(
    {
      className: ['lk-control-bar', isSidebarOpen ? 'lk-control-bar-compact' : null]
        .filter(Boolean)
        .join(' '),
    },
    props,
  )

  const defaultPersistentUserChoices = useMemo(
    () => ({ preventSave: !saveUserChoices }),
    [saveUserChoices],
  )

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices(defaultPersistentUserChoices)

  const microphoneOnChange = useCallback(
    (enabled: boolean, isUserInitiated: boolean) =>
      isUserInitiated ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled],
  )

  const cameraOnChange = useCallback(
    (enabled: boolean, isUserInitiated: boolean) => {
      return isUserInitiated ? saveVideoInputEnabled(enabled) : null
    },
    [saveVideoInputEnabled],
  )

  const { text_primary_color } = useAppSelector(selectBranding)

  // Группы и текущая группа пользователя
  const { groups, getCurrentUserGroupId } = useGroups()
  const currentGroupId = getCurrentUserGroupId
  const currentGroup = useMemo(
    () => groups.find((group) => group.id === currentGroupId) ?? null,
    [groups, currentGroupId],
  )
  const { remainingTime: currentGroupRemainingTime } = useGroupRemainingTime(
    room.metadata,
    !!currentGroup,
  )

  const handleLeaveClick = () => {
    setIsPopupOpen(true)
  }

  const handlePopupClose = useCallback(() => {
    setIsPopupOpen(false)
  }, [])

  const handlePopupConfirm = useCallback(() => {
    setIsPopupOpen(false)
    dispatch(closeUnavailableRecordingPopup())
    room.disconnect()
  }, [room, dispatch])

  const popupClose = useCallback(() => {
    dispatch(closeUnavailableRecordingPopup())
  }, [dispatch])

  const audioDeviceChange = useCallback(
    (_kind: MediaDeviceKind, deviceId: string) => saveAudioInputDeviceId(deviceId ?? ''),
    [saveAudioInputDeviceId],
  )

  const videoDeviceChange = useCallback(
    (_kind: MediaDeviceKind, deviceId: string) => saveVideoInputDeviceId(deviceId ?? ''),
    [saveVideoInputDeviceId],
  )

  return (
    <div {...htmlProps}>
      {!isPiP && <BrandLogo />}

      <div className="lk-button-group-custom">
        {visibleControls.microphone && (
          <div className="lk-button-group" style={{ position: 'relative' }}>
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
              isPiP={isPiP}
              skipWarmUp={visibleControls.skipMicWarmUp}
            >
              {showText && t('micLabel')}
            </TrackToggle>

            {!isPiP && (
              <div className="lk-button-group-menu">
                <MediaDeviceMenu kind="audioinput" onActiveDeviceChange={audioDeviceChange} />
              </div>
            )}
          </div>
        )}

        {visibleControls.camera && (
          <div className="lk-button-group" style={{ position: 'relative' }}>
            <TrackToggle source={Track.Source.Camera} showIcon={showIcon} onChange={cameraOnChange}>
              {showText && t('camLabel')}
            </TrackToggle>

            {!isPiP && (
              <div className="lk-button-group-menu">
                <MediaDeviceMenu
                  kind="videoinput"
                  onActiveDeviceChange={videoDeviceChange}
                  enableVideoEffects
                />
              </div>
            )}
          </div>
        )}

        {visibleControls.screenShare && browserSupportsScreenSharing && (
          <TrackToggle
            // className="lk-hidden-mobile"
            source={Track.Source.ScreenShare}
            captureOptions={screenShareProfile.captureOptions}
            publishOptions={screenShareProfile.publishOptions}
            showIcon={showIcon}
            onChange={onScreenShareChange}
          >
            {showText && (isScreenShareEnabled ? t('disableScreenShare') : t('screenShare'))}
          </TrackToggle>
        )}

        {visibleControls.record && (
          <RecordButton>
            {spinning && <Spinner color={text_primary_color} width={30} />}
            {room.isRecording ? <RecordCircleIcon fill="red" /> : <RecordCircleDisabledIcon />}
            {showText && t('record')}
          </RecordButton>
        )}

        {visibleControls.participants && !isMiddleDeviceByWidth && (
          <ParticipantsButton>
            {showIcon && <UsersIcon fill="white" />}
            {showText && t('participants')}
          </ParticipantsButton>
        )}

        {showQuestionsButton && !isPiP && (
          <button
            type="button"
            className="lk-button"
            onClick={onToggleQuestions}
            aria-pressed={isQuestionsOpen}
          >
            {showIcon && <QuestionsControlIcon fill={text_primary_color ?? '#fff'} />}
            {showText && 'Вопросы'}
          </button>
        )}

        {visibleControls.chat && !isMiddleDeviceByWidth && (
          <ChatToggle>
            {showIcon && <ChatIcon fill={text_primary_color ?? '#fff'} />}
            {showText && t('chat.title')}
          </ChatToggle>
        )}

        {visibleControls.raiseHandOnly && (
          <RaiseHandButton />
        )}

        {!visibleControls.raiseHandOnly &&
          (isPiP || (visibleControls.reactions && !isMiddleDeviceByWidth)) && (
          <div className="lk-button-group" style={{ position: 'relative' }}>
            <ReactionToggle isPiP={isPiP} />
          </div>
        )}

        {isMiddleDeviceByWidth && !isPiP && !visibleControls.raiseHandOnly && (
          <MoreButton
            isOpened={isOpenedMoreBtnPopover}
            waitingParticipants={
              v === 'hr' && visibleControls.participants && (isAdmin || localIsCurator)
                ? (layoutContext?.widget?.state?.waitingParticipants ?? 0)
                : 0
            }
            togglePopover={handleToggleMoreBtnPopover}
          >
            {visibleControls.reactions && (
              <Reactions className="more-btn-reactions" closePopover={handleCloseMoreBtnPopover} />
            )}

            {visibleControls.participants && (
              <ParticipantsButton>
                <UsersIcon fill="white" />
                {t('participants')}
              </ParticipantsButton>
            )}

            {visibleControls.chat && (
              <ChatToggle>
                <ChatIcon />
                {t('chat.title')}
              </ChatToggle>
            )}
          </MoreButton>
        )}

        {visibleControls.leave && (
          <div onClick={handleLeaveClick} style={{ display: 'flex' }}>
            <DisconnectButton>
              {showIcon && <LeaveIcon fill={text_primary_color ?? '#fff'} />}
              {showText && t('leave')}
            </DisconnectButton>
          </div>
        )}

        <StartMediaButton />

        {/* Подпись названия группы для всех участников */}
        {currentGroup && (
          <div
            className="lk-group-name-label"
            style={{
              marginLeft: '1rem',
              color: text_primary_color,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              lineHeight: 1.2,
            }}
          >
            <span>{currentGroup.name}</span>
            {currentGroupRemainingTime && (
              <span
                style={{
                  fontSize: '12px',
                  opacity: 0.8,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('groupTimeLeft', { time: currentGroupRemainingTime })}
              </span>
            )}
          </div>
        )}

        {isPopupOpen && <Popup onClose={handlePopupClose} onConfirm={handlePopupConfirm} />}

        {isOpenUnavailableRecordingPopup && (
          <InformationRecordingMeeting onClose={popupClose} onConfirm={handlePopupConfirm} />
        )}

        <MvpPopup />
      </div>
    </div>
  )
}
